// XPHistoryPage Component
// Full page displaying student's XP history and referral widget

import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getXPSummary, getXPTransactions } from '../../xpService';
import { getWalletItems } from '../../walletService';
import { supabase } from '../../supabaseClient';
import { toArabicNumerals, translateSubject } from '../../utils/arabicNumbers';
import { FaBook, FaStar, FaBullseye, FaFire, FaGift, FaCog, FaGlassCheers, FaCheckCircle, FaMagic, FaChartBar } from 'react-icons/fa';
import ProgressBar from './ProgressBar';
import ReferralWidget from './ReferralWidget';
import WalletWidget from './WalletWidget';

// Brand colors - matching StudentDashboard
const COLORS = {
  light: '#FFF8F1',
  dark: '#1A2341',
  orange: '#FF7A1A',
  orangeLight: '#f3b68a',
  blue: '#22305A',
  white: '#FFFFFF',
  gray: '#F5F7FA',
  textSecondary: '#64748B',
  borderLight: '#E2E8F0'
};

export default function XPHistoryPage() {
  const { isArabic } = useOutletContext();
  const [xpData, setXpData] = useState({
    total_xp: 0,
    current_level: 1,
    xp_to_next_level: 50
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState(null);
  const [unclaimedCount, setUnclaimedCount] = useState(0);
  const navigate = useNavigate();
  const isMobile = window.innerWidth <= 600;

  useEffect(() => {
    async function fetchStudentId() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setStudentId(user.id);
      } else {
        navigate('/signin');
      }
    }
    fetchStudentId();
  }, [navigate]);

  useEffect(() => {
    async function fetchData() {
      if (!studentId) return;

      setLoading(true);

      // Fetch XP summary
      const { data: summaryData, error: summaryError } = await getXPSummary(studentId);
      if (summaryData && !summaryError) {
        setXpData({
          total_xp: summaryData.total_xp || 0,
          current_level: summaryData.current_level || 1,
          xp_to_next_level: summaryData.xp_to_next_level || 50
        });
      }

      // Fetch transactions (fetch all, no pagination for now)
      const { data: transData, error: transError } = await getXPTransactions(studentId, 100, 0);
      if (transData && !transError) {
        setTransactions(transData.transactions || []);
      }

      setLoading(false);
    }

    fetchData();
  }, [studentId]);

  // Load unclaimed rewards count
  useEffect(() => {
    async function loadUnclaimedCount() {
      if (!studentId) return;

      const { data } = await getWalletItems('unclaimed');
      setUnclaimedCount(data?.length || 0);
    }

    loadUnclaimedCount();
  }, [studentId]);

  const getTypeLabel = (type) => {
    const labels = {
      session_complete: isArabic ? 'جلسة مكتملة' : 'Session Complete',
      rating_given: isArabic ? 'تقييم معطى' : 'Rating Given',
      new_subject: isArabic ? 'مادة جديدة' : 'New Subject',
      weekly_streak: isArabic ? 'سلسلة أسبوعية' : 'Weekly Streak',
      referral: isArabic ? 'إحالة صديق' : 'Friend Referral',
      manual_adjustment: isArabic ? 'تعديل يدوي' : 'Manual Adjustment',
      signup_bonus: isArabic ? 'مكافأة التسجيل' : 'Signup Bonus',
      profile_completion_bonus: isArabic ? 'مكافأة إكمال الملف' : 'Profile Completion Bonus'
    };

    // If the type is not in labels, format it by replacing underscores and capitalizing
    if (!labels[type]) {
      return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return labels[type];
  };

  const getTypeIcon = (type) => {
    const icons = {
      session_complete: FaBook,
      rating_given: FaStar,
      new_subject: FaBullseye,
      weekly_streak: FaFire,
      referral: FaGift,
      manual_adjustment: FaCog,
      signup_bonus: FaGlassCheers,
      profile_completion_bonus: FaCheckCircle
    };
    return icons[type] || FaMagic;
  };

  const getTypeColor = (type) => {
    // Return orange color for all transaction types
    return COLORS.orange;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return isArabic ? 'الآن' : 'Just now';
    if (diffMins < 60) return isArabic ? `منذ ${toArabicNumerals(diffMins, true)} دقيقة` : `${diffMins}m ago`;
    if (diffHours < 24) return isArabic ? `منذ ${toArabicNumerals(diffHours, true)} ساعة` : `${diffHours}h ago`;
    if (diffDays < 7) return isArabic ? `منذ ${toArabicNumerals(diffDays, true)} يوم` : `${diffDays}d ago`;

    return date.toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const xpInCurrentLevel = 50 - xpData.xp_to_next_level;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f7f9fb',
      padding: isMobile ? '20px' : '40px 20px'
    }}>
      <div style={{
        maxWidth: 1200,
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          marginBottom: 32
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              fontSize: isMobile ? 14 : 16,
              cursor: 'pointer',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 0
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = COLORS.orange}
            onMouseLeave={(e) => e.currentTarget.style.color = COLORS.textSecondary}
          >
            ← {isArabic ? 'رجوع' : 'Back'}
          </button>

          <h1 style={{
            fontSize: isMobile ? 28 : 36,
            fontWeight: 800,
            color: '#1e293b',
            margin: 0
          }}>
            {isArabic ? 'سجل نقاط الخبرة' : 'XP History'}
          </h1>
        </div>

        {/* XP Summary Card */}
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
          borderRadius: isMobile ? 16 : 24,
          padding: isMobile ? 20 : 32,
          boxShadow: '0 8px 32px rgba(255, 116, 38, 0.3)',
          marginBottom: 24
        }}>
          {isMobile ? (
            // Mobile Layout - Centered and Organized
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 20
            }}>
              {/* Total XP Section */}
              <div style={{ width: '100%' }}>
                <div style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: 8,
                  fontWeight: 600
                }}>
                  {isArabic ? 'إجمالي XP' : 'Total XP'}
                </div>
                <div style={{
                  fontSize: 56,
                  fontWeight: 800,
                  color: '#fff',
                  lineHeight: 1
                }}>
                  {loading ? '...' : toArabicNumerals(xpData.total_xp.toLocaleString(), isArabic)}
                </div>
              </div>

              {/* Divider */}
              <div style={{
                width: '100%',
                height: 1,
                background: 'rgba(255,255,255,0.2)'
              }} />

              {/* Current Level Section */}
              <div style={{ width: '100%' }}>
                <div style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: 8,
                  fontWeight: 600
                }}>
                  {isArabic ? 'المستوى الحالي' : 'Current Level'}
                </div>
                <div style={{
                  fontSize: 36,
                  fontWeight: 800,
                  color: '#fff',
                  marginBottom: 16
                }}>
                  {isArabic ? 'المستوى' : 'Level'} {toArabicNumerals(xpData.current_level, isArabic)}
                </div>
                <ProgressBar
                  current={xpInCurrentLevel}
                  max={50}
                  color="#FFF"
                  bgColor="rgba(255,255,255,0.3)"
                  height={8}
                />
                <div style={{
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.9)',
                  marginTop: 8,
                  fontWeight: 600
                }}>
                  {toArabicNumerals(xpData.xp_to_next_level, isArabic)} XP {isArabic ? 'للمستوى التالي' : 'to next level'}
                </div>
              </div>
            </div>
          ) : (
            // Desktop Layout - Side by Side
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 24,
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 16,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: 8
                }}>
                  {isArabic ? 'إجمالي XP' : 'Total XP'}
                </div>
                <div style={{
                  fontSize: 64,
                  fontWeight: 800,
                  color: '#fff',
                  lineHeight: 1
                }}>
                  {loading ? '...' : toArabicNumerals(xpData.total_xp.toLocaleString(), isArabic)}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 16,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: 8
                }}>
                  {isArabic ? 'المستوى الحالي' : 'Current Level'}
                </div>
                <div style={{
                  fontSize: 40,
                  fontWeight: 800,
                  color: '#fff',
                  marginBottom: 12
                }}>
                  {isArabic ? 'المستوى' : 'Level'} {toArabicNumerals(xpData.current_level, isArabic)}
                </div>
                <ProgressBar
                  current={xpInCurrentLevel}
                  max={50}
                  color="#FFF"
                  bgColor="rgba(255,255,255,0.3)"
                  height={8}
                />
                <div style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.9)',
                  marginTop: 8
                }}>
                  {toArabicNumerals(xpData.xp_to_next_level, isArabic)} XP {isArabic ? 'للمستوى التالي' : 'to next level'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Grid Layout */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24
        }}>
          {/* Desktop: Two Column Layout */}
          {!isMobile && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: 24
            }}>
              {/* Left Column - Transactions and Wallet */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 24
              }}>
                {/* Transactions Timeline */}
                <div style={{
                  background: COLORS.white,
                  borderRadius: 20,
                  padding: 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  border: `1px solid ${COLORS.borderLight}`,
                  minHeight: 400
                }}>
                  {/* Title */}
                  <div style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: COLORS.dark,
                    marginBottom: 20,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    paddingBottom: 16,
                    borderBottom: `2px solid ${COLORS.borderLight}`
                  }}>
                    <FaChartBar style={{ fontSize: 28, color: COLORS.dark }} />
                    <span>{isArabic ? 'سجل المعاملات' : 'Transaction History'}</span>
                  </div>

                  {loading ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 200,
                      color: COLORS.textSecondary
                    }}>
                      {isArabic ? 'جاري التحميل...' : 'Loading...'}
                    </div>
                  ) : transactions.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 200,
                      textAlign: 'center'
                    }}>
                      <FaChartBar style={{ fontSize: 48, marginBottom: 12, color: COLORS.textSecondary }} />
                      <div style={{ color: COLORS.dark, fontSize: 16, marginBottom: 4 }}>
                        {isArabic ? 'لا توجد معاملات' : 'No Transactions'}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: 14 }}>
                        {isArabic ? 'ابدأ بإكمال الجلسات لكسب XP' : 'Start completing sessions to earn XP'}
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12
                    }}>
                      {transactions.slice(0, 3).map((transaction, idx) => (
                    <div
                      key={transaction.id || idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        padding: isMobile ? 12 : 16,
                        background: COLORS.gray,
                        borderRadius: 12,
                        borderLeft: `4px solid ${getTypeColor(transaction.xp_type)}`,
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = COLORS.light;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = COLORS.gray;
                      }}
                    >
                      <div style={{ fontSize: isMobile ? 28 : 32, color: getTypeColor(transaction.xp_type) }}>
                        {React.createElement(getTypeIcon(transaction.xp_type))}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: isMobile ? 14 : 15,
                          fontWeight: 600,
                          color: COLORS.dark,
                          marginBottom: 4
                        }}>
                          {getTypeLabel(transaction.xp_type)}
                        </div>
                        <div style={{
                          fontSize: isMobile ? 11 : 12,
                          color: COLORS.textSecondary
                        }}>
                          {formatDate(transaction.created_at)}
                          {transaction.metadata?.teacher_name && (
                            <span> • {transaction.metadata.teacher_name}</span>
                          )}
                          {transaction.metadata?.subject && (
                            <span> • {translateSubject(transaction.metadata.subject, isArabic)}</span>
                          )}
                        </div>
                      </div>
                      <div style={{
                        fontSize: isMobile ? 18 : 20,
                        fontWeight: 800,
                        color: getTypeColor(transaction.xp_type)
                      }}>
                        +{toArabicNumerals(transaction.xp_amount, isArabic)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Wallet Widget - Moved to left column */}
            <WalletWidget studentId={studentId} isArabic={isArabic} />
          </div>

          {/* Right Column - Widgets */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24
          }}>
            {/* Referral Widget */}
            <ReferralWidget isArabic={isArabic} />

            {/* How to Earn More XP Widget */}
            <div style={{
              background: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
              borderRadius: 20,
              padding: isMobile ? 20 : 24,
              boxShadow: '0 8px 32px rgba(255, 116, 38, 0.3)',
              color: '#fff'
            }}>
              <div style={{
                fontSize: isMobile ? 16 : 18,
                fontWeight: 800,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <FaBullseye style={{ fontSize: isMobile ? 20 : 24 }} />
                <span>{isArabic ? 'كيفية كسب المزيد من XP' : 'How to Earn More XP'}</span>
              </div>

              {/* Scrollable List */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                maxHeight: 320,
                overflowY: 'auto',
                paddingRight: 8,
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255,255,255,0.3) transparent'
              }}>
                <style>
                  {`
                    div::-webkit-scrollbar {
                      width: 6px;
                    }
                    div::-webkit-scrollbar-track {
                      background: transparent;
                    }
                    div::-webkit-scrollbar-thumb {
                      background: rgba(255,255,255,0.3);
                      borderRadius: 3px;
                    }
                    div::-webkit-scrollbar-thumb:hover {
                      background: rgba(255,255,255,0.5);
                    }
                  `}
                </style>
                {[
                  {
                    Icon: FaBook,
                    title: isArabic ? 'أكمل الجلسات' : 'Complete Sessions',
                    xp: '+10 XP'
                  },
                  {
                    Icon: FaStar,
                    title: isArabic ? 'قيّم المعلمين' : 'Rate Teachers',
                    xp: '+5 XP'
                  },
                  {
                    Icon: FaBullseye,
                    title: isArabic ? 'جرّب مواد جديدة' : 'Try New Subjects',
                    xp: '+15 XP'
                  },
                  {
                    Icon: FaFire,
                    title: isArabic ? 'حافظ على سلسلة' : 'Weekly Streak',
                    xp: '+20 XP'
                  },
                  {
                    Icon: FaGift,
                    title: isArabic ? 'أحِل صديقًا' : 'Refer a Friend',
                    xp: '+200 XP'
                  }
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: 'rgba(255,255,255,0.15)',
                      borderRadius: 12,
                      padding: isMobile ? 12 : 14,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      transition: 'all 0.2s ease',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div style={{
                      fontSize: isMobile ? 28 : 32,
                      lineHeight: 1
                    }}>
                      <item.Icon />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: isMobile ? 13 : 14,
                        fontWeight: 700,
                        marginBottom: 2
                      }}>
                        {item.title}
                      </div>
                    </div>
                    <div style={{
                      fontSize: isMobile ? 16 : 18,
                      fontWeight: 900,
                      background: 'rgba(255,255,255,0.3)',
                      padding: '6px 12px',
                      borderRadius: 8
                    }}>
                      {item.xp}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

          {/* Mobile: Stacked Layout */}
          {isMobile && (
            <>
              {/* Transactions Timeline */}
              <div style={{
                background: COLORS.white,
                borderRadius: 16,
                padding: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: `1px solid ${COLORS.borderLight}`,
                minHeight: 300
              }}>
                {/* Title */}
                <div style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: COLORS.dark,
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  paddingBottom: 12,
                  borderBottom: `2px solid ${COLORS.borderLight}`
                }}>
                  <FaChartBar style={{ fontSize: 20, color: COLORS.dark }} />
                  <span>{isArabic ? 'سجل المعاملات' : 'Transaction History'}</span>
                </div>

                {loading ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 150,
                    color: COLORS.textSecondary
                  }}>
                    {isArabic ? 'جاري التحميل...' : 'Loading...'}
                  </div>
                ) : transactions.length === 0 ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 150,
                    textAlign: 'center'
                  }}>
                    <FaChartBar style={{ fontSize: 40, marginBottom: 8, color: COLORS.textSecondary }} />
                    <div style={{ color: COLORS.dark, fontSize: 14, marginBottom: 4 }}>
                      {isArabic ? 'لا توجد معاملات' : 'No Transactions'}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                      {isArabic ? 'ابدأ بإكمال الجلسات لكسب XP' : 'Start completing sessions to earn XP'}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}>
                    {transactions.slice(0, 3).map((transaction, idx) => (
                      <div
                        key={transaction.id || idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: 10,
                          background: COLORS.gray,
                          borderRadius: 10,
                          borderLeft: `3px solid ${getTypeColor(transaction.xp_type)}`
                        }}
                      >
                        <div style={{ fontSize: 24, color: getTypeColor(transaction.xp_type) }}>
                          {React.createElement(getTypeIcon(transaction.xp_type))}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: COLORS.dark,
                            marginBottom: 2
                          }}>
                            {getTypeLabel(transaction.xp_type)}
                          </div>
                          <div style={{
                            fontSize: 10,
                            color: COLORS.textSecondary
                          }}>
                            {formatDate(transaction.created_at)}
                          </div>
                        </div>
                        <div style={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: getTypeColor(transaction.xp_type)
                        }}>
                          +{toArabicNumerals(transaction.xp_amount, isArabic)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Referral Widget */}
              <ReferralWidget isArabic={isArabic} />

              {/* Wallet Widget */}
              <WalletWidget studentId={studentId} isArabic={isArabic} />

              {/* How to Earn More XP Widget - Compact */}
              <div style={{
                background: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
                borderRadius: 16,
                padding: 16,
                boxShadow: '0 4px 16px rgba(255, 116, 38, 0.3)',
                color: '#fff'
              }}>
                <div style={{
                  fontSize: 15,
                  fontWeight: 800,
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <FaBullseye style={{ fontSize: 18 }} />
                  <span>{isArabic ? 'كيفية كسب المزيد من XP' : 'How to Earn More XP'}</span>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8
                }}>
                  {[
                    {
                      Icon: FaBook,
                      title: isArabic ? 'أكمل الجلسات' : 'Complete Sessions',
                      xp: '+10 XP'
                    },
                    {
                      Icon: FaStar,
                      title: isArabic ? 'قيّم المعلمين' : 'Rate Teachers',
                      xp: '+5 XP'
                    },
                    {
                      Icon: FaBullseye,
                      title: isArabic ? 'جرّب مواد جديدة' : 'Try New Subjects',
                      xp: '+15 XP'
                    },
                    {
                      Icon: FaFire,
                      title: isArabic ? 'حافظ على سلسلة' : 'Weekly Streak',
                      xp: '+20 XP'
                    },
                    {
                      Icon: FaGift,
                      title: isArabic ? 'أحِل صديقًا' : 'Refer a Friend',
                      xp: '+200 XP'
                    }
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 10,
                        padding: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        border: '1px solid rgba(255,255,255,0.2)'
                      }}
                    >
                      <div style={{
                        fontSize: 24,
                        lineHeight: 1
                      }}>
                        <item.Icon />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: 12,
                          fontWeight: 700
                        }}>
                          {item.title}
                        </div>
                      </div>
                      <div style={{
                        fontSize: 14,
                        fontWeight: 900,
                        background: 'rgba(255,255,255,0.3)',
                        padding: '4px 10px',
                        borderRadius: 6
                      }}>
                        {item.xp}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
